import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { Params, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { INavigationEnvironment } from 'projects/shared/lib/interfaces/environment/navigation/navigation-environment.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { IIdpApiService, IIdpApiServiceToken } from 'projects/shared/lib/services/api/idp/idp-api-service.interface';
import { IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { ISocialMediaEntryEnvironment } from 'projects/shared/lib/interfaces/environment/social/social-entry-environment.interface';
import { ExternalLinksComponent } from '../../external-links/external-links.component';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Component({
  selector: 'app-sidebar-user',
  imports: [
    RouterModule,
    ExternalLinksComponent
  ],
  templateUrl: './sidebar-user.component.html',
  styleUrls: ['./sidebar-user.component.scss']
})
export class SidebarUserComponent implements OnInit, OnDestroy {
  public loggedIn: boolean = false;
  private authSubscription: Subscription | undefined;
  public anonymousLandingPage: INavigationEnvironment;

  public isOrderCheckoutAllowed: boolean;
  public isOfferCheckoutAllowed: boolean;

  public displayProductListRecentlyLink: boolean;

  public webshopUserSidebarMenu: boolean;
  public clientPortalUserSidebarMenu: boolean;

  protected logger: LoggerService;
  protected router: Router;
  protected readonly authService: IAuthService;
  protected readonly userService: IUserService;
  protected readonly toastService: IToastService = inject(IToastServiceToken);
  protected readonly idpApiService: IIdpApiService | null;

  public externalLinkList: ISocialMediaEntryEnvironment[] | undefined;

  public constructor() {
    this.anonymousLandingPage = ENVIRONMENT.anonymousLandingPage;
    ServiceLocator.injector.get(IConfigurationServiceToken).anonymousLandingPageGet().subscribe((anonymousLandingPage) => {
      this.anonymousLandingPage.routerlink = anonymousLandingPage.routerlink as string;
      let queryParams = new URLSearchParams(anonymousLandingPage.queryParams);
      let queryParamsObject: Params = {};
      queryParams.forEach((value, key) => {
        queryParamsObject[key] = value;
      });
      this.anonymousLandingPage.queryParams = queryParamsObject;
    });

    this.isOrderCheckoutAllowed = ENVIRONMENT.checkout.isOrderCheckoutAllowed;
    this.isOfferCheckoutAllowed = ENVIRONMENT.checkout.isOfferCheckoutAllowed;
    this.externalLinkList = ENVIRONMENT.user.externalLinkList;

    this.displayProductListRecentlyLink = ENVIRONMENT.user.displayProductListRecentlyLink != undefined ? ENVIRONMENT.user.displayProductListRecentlyLink : true;

    this.webshopUserSidebarMenu = ENVIRONMENT.user.webshopUserSidebarMenu != undefined ? ENVIRONMENT.user.webshopUserSidebarMenu : true;
    this.clientPortalUserSidebarMenu = ENVIRONMENT.user.clientPortalUserSidebarMenu != undefined ? ENVIRONMENT.user.clientPortalUserSidebarMenu : false;

    this.logger = ServiceLocator.injector.get(LoggerService);
    this.router = ServiceLocator.injector.get(Router);
    this.authService = inject(IAuthServiceToken);
    this.userService = inject(IUserServiceToken);
    this.idpApiService = inject(IIdpApiServiceToken, { optional: true });

    ServiceLocator.injector.get(IConfigurationServiceToken).checkoutConfigurationGet().subscribe((checkoutConfiguration) => {
      if (checkoutConfiguration.isOrderCheckoutAllowed != undefined) {
        this.isOrderCheckoutAllowed = checkoutConfiguration.isOrderCheckoutAllowed;
      }
      if (checkoutConfiguration.isOfferCheckoutAllowed != undefined) {
        this.isOfferCheckoutAllowed = checkoutConfiguration.isOfferCheckoutAllowed;
      }
    });
  }

  public ngOnInit(): void {
    this.loggedIn = this.authService.isAuthenticated();

    if (!this.authSubscription) {
      this.authSubscription = this.authService.authenticatedGet().subscribe(
        (isAuthenticated: boolean) => {
          this.loggedIn = isAuthenticated;
        }
      );
    }
  }

  public ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  public logout() {
    this.idpApiService?.logout();

    this.userService.userLogout().subscribe({
      next: () => {
        this.authService.accessJWTClear();

        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Success,
          title: $localize`:@@NavTopMenuLogoutSuccessToastTitle:Logout successful`,
        }));

        this.router.navigate([this.anonymousLandingPage.routerlink], { queryParams: this.anonymousLandingPage.queryParams });
      },
      error: (ex) => {
        this.logger.error(ex, this.constructor.name);
        let errorMessage = $localize`:@@NavTopMenuLogoutErrorToastTitle:Logout failed`;
        throw new BasicError(errorMessage, JSON.parse(ex.response).errorCode);
      },
      complete: () => { }
    });
  }

}
